"use client";

import React, { useState } from "react";
import { 
  X, 
  Scale, 
  ShieldCheck, 
  UploadCloud, 
  CheckCircle2, 
  AlertCircle, 
  ArrowRight,
  Lock,
  FileText
} from "lucide-react";
import { PRACTICE_AREAS, PracticeAreaInfo } from "@/lib/demos/legalData";

interface CaseEvaluationModalProps {
  isOpen: boolean;
  onClose: () => void;
  currency: "INR" | "USD";
  initialPracticeArea?: PracticeAreaInfo | null;
  onCaseCreated?: (newCase: any) => void;
}

export default function CaseEvaluationModal({
  isOpen,
  onClose,
  currency,
  initialPracticeArea,
  onCaseCreated,
}: CaseEvaluationModalProps) {
  const [clientName, setClientName] = useState("");
  const [clientEmail, setClientEmail] = useState("");
  const [clientPhone, setClientPhone] = useState("");
  const [matterTitle, setMatterTitle] = useState("");
  const [selectedPracticeArea, setSelectedPracticeArea] = useState<string>(
    initialPracticeArea?.title || PRACTICE_AREAS[0].title
  );
  const [opposingParty, setOpposingParty] = useState("");
  const [urgency, setUrgency] = useState<"EMERGENCY" | "HIGH" | "NORMAL">("NORMAL");
  const [notes, setNotes] = useState("");
  const [hasUploadedFile, setHasUploadedFile] = useState(false);
  const [fileName, setFileName] = useState("");

  const [loading, setLoading] = useState(false);
  const [submittedCase, setSubmittedCase] = useState<any | null>(null);
  const [errorMsg, setErrorMsg] = useState("");

  if (!isOpen) return null;

  // Calculate dynamic retainer estimate
  const currentAreaInfo = PRACTICE_AREAS.find((p) => p.title === selectedPracticeArea) || PRACTICE_AREAS[0];
  const baseRetainer = currency === "USD" ? currentAreaInfo.indicativeRetainerUSD : currentAreaInfo.indicativeRetainerINR;
  const multiplier = urgency === "EMERGENCY" ? 1.6 : urgency === "HIGH" ? 1.25 : 1.0;
  const estimatedRetainer = Math.round(baseRetainer * multiplier);

  const formattedRetainer = currency === "USD"
    ? `$${estimatedRetainer.toLocaleString()}`
    : `₹${estimatedRetainer.toLocaleString("en-IN")}`;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!clientName || !matterTitle) {
      setErrorMsg("Please provide your name and a brief title for your legal matter.");
      return;
    }

    setLoading(true);
    setErrorMsg("");

    try {
      const documents = hasUploadedFile
        ? [
            {
              id: `doc-${Date.now()}`,
              name: fileName || "Client_Brief_Annexures.pdf",
              type: "EVIDENCE",
              fileSize: "2.8 MB",
              uploadedAt: new Date().toISOString().split("T")[0],
              status: "Drafted",
            },
          ]
        : [];

      const payload = {
        title: matterTitle,
        client_name: clientName,
        client_email: clientEmail || "client@inquiry.law",
        client_phone: clientPhone || "+91 98808 93416",
        opposing_party: opposingParty || "Unnamed Adverse Party",
        practice_area: selectedPracticeArea,
        court_name: "High Court of Delhi",
        court_room: "Court Room 14",
        judge_bench: "Hon'ble Bench",
        next_hearing_date: new Date(Date.now() + 7 * 86400000).toISOString().split("T")[0],
        item_number: 14,
        stage: "New Case Intake",
        urgency,
        retainer_amount: estimatedRetainer,
        total_billed_amount: 0,
        currency,
        lead_counsel: "Adv. Johnson & Associates",
        notes: notes || "Intake inquiry ingested via confidential website portal.",
        conflict_check_passed: 1,
        documents,
        appearances: [],
      };

      const res = await fetch("/api/demos/legal/cases", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });

      if (!res.ok) {
        throw new Error("Failed to register case inquiry.");
      }

      const data = await res.json();
      setSubmittedCase(data.case);
      if (onCaseCreated) onCaseCreated(data.case);
    } catch (err: any) {
      console.error(err);
      setErrorMsg(err.message || "Failed to submit case evaluation.");
    } finally {
      setLoading(false);
    }
  };

  const handleSimulateFileUpload = () => {
    setHasUploadedFile(true);
    setFileName("Evidentiary_Documents_And_Notices.pdf");
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-3 sm:p-4 bg-slate-950/80 backdrop-blur-md overflow-y-auto animate-fade-in">
      <div className="relative w-full max-w-2xl p-5 sm:p-8 rounded-2xl bg-slate-900 border border-slate-800 shadow-2xl text-slate-100 my-8">
        
        {/* Close Button */}
        <button
          type="button"
          onClick={onClose}
          className="absolute top-4 right-4 p-2 rounded-xl text-slate-400 hover:text-white hover:bg-slate-800 transition-colors"
        >
          <X className="w-5 h-5" />
        </button>

        {submittedCase ? (
          /* Case Ingestion Success Screen */
          <div className="space-y-6 text-center py-4">
            <div className="w-14 h-14 mx-auto rounded-full bg-emerald-500/10 border border-emerald-500/30 flex items-center justify-center text-emerald-400">
              <CheckCircle2 className="w-8 h-8" />
            </div>

            <div>
              <span className="text-[11px] font-bold px-2.5 py-1 rounded-full bg-emerald-500/10 text-emerald-400 border border-emerald-500/20 uppercase tracking-wider">
                Intake Logged & Conflict Cleared
              </span>
              <h3 className="text-xl sm:text-2xl font-serif font-bold text-white mt-2">
                Preliminary Case Evaluation Registered
              </h3>
              <p className="text-xs text-slate-400 max-w-md mx-auto mt-1">
                Your matter has been ingested into LexJuris Chambers' case management pipeline with automated conflict check clearance.
              </p>
            </div>

            {/* Generated Docket Summary Card */}
            <div className="p-4 rounded-xl bg-slate-850 border border-slate-750 text-left space-y-2 text-xs">
              <div className="flex items-center justify-between">
                <span className="text-slate-400">Assigned Case No:</span>
                <span className="font-mono text-amber-400 font-bold">{submittedCase.case_number}</span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-slate-400">Simulated CNR:</span>
                <span className="font-mono text-white font-semibold">{submittedCase.cnr_number}</span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-slate-400">Practice Domain:</span>
                <span className="text-white font-medium">{submittedCase.practice_area}</span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-slate-400">Estimated Retainer:</span>
                <span className="text-emerald-400 font-bold font-mono">
                  {currency === "USD" ? `$${submittedCase.retainer_amount}` : `₹${submittedCase.retainer_amount.toLocaleString("en-IN")}`}
                </span>
              </div>
              <div className="flex items-center justify-between pt-2 border-t border-slate-700/60">
                <span className="text-slate-400">Conflict Check:</span>
                <span className="text-emerald-400 font-semibold flex items-center gap-1">
                  <ShieldCheck className="w-3.5 h-3.5" /> No Adverse Representation Found
                </span>
              </div>
            </div>

            {/* Action Buttons */}
            <div className="flex flex-col sm:flex-row items-center justify-center gap-3 pt-2">
              <a
                href="/demos/legal/crm"
                className="w-full sm:w-auto px-6 py-3 rounded-xl bg-gradient-to-r from-amber-500 to-amber-600 hover:from-amber-400 hover:to-amber-500 text-slate-950 font-bold text-xs shadow-lg transition-all flex items-center justify-center gap-2"
              >
                <span>Track Matter in Attorney CRM</span>
                <ArrowRight className="w-4 h-4" />
              </a>
              <button
                type="button"
                onClick={onClose}
                className="w-full sm:w-auto px-5 py-3 rounded-xl bg-slate-800 hover:bg-slate-700 text-slate-300 text-xs font-semibold"
              >
                Done
              </button>
            </div>
          </div>
        ) : (
          /* Intake Evaluation Form */
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="flex items-center gap-2.5 pb-3 border-b border-slate-800">
              <div className="p-2 rounded-xl bg-amber-500/10 border border-amber-500/20 text-amber-400">
                <Scale className="w-5 h-5" />
              </div>
              <div>
                <h3 className="text-base sm:text-lg font-serif font-bold text-white">
                  Confidential Case Evaluation & Retainer Intake
                </h3>
                <p className="text-[11px] text-slate-400">
                  Preliminary assessment under strict attorney-client privilege
                </p>
              </div>
            </div>

            {errorMsg && (
              <div className="p-3 rounded-xl bg-rose-500/10 border border-rose-500/20 text-xs text-rose-400 flex items-center gap-2">
                <AlertCircle className="w-4 h-4 shrink-0" />
                <span>{errorMsg}</span>
              </div>
            )}

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3.5">
              {/* Client Name */}
              <div>
                <label className="block text-[11px] font-semibold text-slate-300 mb-1">
                  Client / Entity Legal Name *
                </label>
                <input
                  type="text"
                  value={clientName}
                  onChange={(e) => setClientName(e.target.value)}
                  placeholder="e.g. Vikramaditya Singhania or Acme Corp"
                  className="w-full px-3 py-2 bg-slate-850 border border-slate-750 rounded-xl text-xs text-white placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-amber-500/50"
                  required
                />
              </div>

              {/* Phone */}
              <div>
                <label className="block text-[11px] font-semibold text-slate-300 mb-1">
                  Secure Phone (WhatsApp Active) *
                </label>
                <input
                  type="tel"
                  value={clientPhone}
                  onChange={(e) => setClientPhone(e.target.value)}
                  placeholder="+91 98765 43210"
                  className="w-full px-3 py-2 bg-slate-850 border border-slate-750 rounded-xl text-xs text-white placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-amber-500/50"
                  required
                />
              </div>

              {/* Email */}
              <div>
                <label className="block text-[11px] font-semibold text-slate-300 mb-1">
                  Confidential Email
                </label>
                <input
                  type="email"
                  value={clientEmail}
                  onChange={(e) => setClientEmail(e.target.value)}
                  placeholder="counsel@enterprise.com"
                  className="w-full px-3 py-2 bg-slate-850 border border-slate-750 rounded-xl text-xs text-white placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-amber-500/50"
                />
              </div>

              {/* Opposing Party Name for Conflict Check */}
              <div>
                <label className="block text-[11px] font-semibold text-slate-300 mb-1 flex items-center justify-between">
                  <span>Opposing Party (Conflict Check)</span>
                  <span className="text-[10px] text-amber-400 font-normal">Mandatory Check</span>
                </label>
                <input
                  type="text"
                  value={opposingParty}
                  onChange={(e) => setOpposingParty(e.target.value)}
                  placeholder="e.g. State of NCT Delhi or XYZ Corp"
                  className="w-full px-3 py-2 bg-slate-850 border border-slate-750 rounded-xl text-xs text-white placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-amber-500/50"
                />
              </div>
            </div>

            {/* Matter Title */}
            <div>
              <label className="block text-[11px] font-semibold text-slate-300 mb-1">
                Matter Title / Nature of Cause of Action *
              </label>
              <input
                type="text"
                value={matterTitle}
                onChange={(e) => setMatterTitle(e.target.value)}
                placeholder="e.g. Urgent Section 9 Stay on Bank Guarantee or Anticipatory Bail"
                className="w-full px-3 py-2 bg-slate-850 border border-slate-750 rounded-xl text-xs text-white placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-amber-500/50"
                required
              />
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3.5">
              {/* Practice Area Selector */}
              <div>
                <label className="block text-[11px] font-semibold text-slate-300 mb-1">
                  Practice Domain
                </label>
                <select
                  value={selectedPracticeArea}
                  onChange={(e) => setSelectedPracticeArea(e.target.value)}
                  className="w-full px-3 py-2 bg-slate-850 border border-slate-750 rounded-xl text-xs text-white focus:outline-none focus:ring-2 focus:ring-amber-500/50"
                >
                  {PRACTICE_AREAS.map((p) => (
                    <option key={p.id} value={p.title}>
                      {p.title}
                    </option>
                  ))}
                </select>
              </div>

              {/* Urgency Level */}
              <div>
                <label className="block text-[11px] font-semibold text-slate-300 mb-1">
                  Urgency / Hearing Requirement
                </label>
                <select
                  value={urgency}
                  onChange={(e) => setUrgency(e.target.value as any)}
                  className="w-full px-3 py-2 bg-slate-850 border border-slate-750 rounded-xl text-xs text-white focus:outline-none focus:ring-2 focus:ring-amber-500/50"
                >
                  <option value="NORMAL">Normal Notice / Advisory (30 Days)</option>
                  <option value="HIGH">Impending Filing / 7 Days</option>
                  <option value="EMERGENCY">⚡ Emergency Stay / Bail (&lt;48 Hours)</option>
                </select>
              </div>
            </div>

            {/* Brief Facts / Notes */}
            <div>
              <label className="block text-[11px] font-semibold text-slate-300 mb-1">
                Brief Synopsis of Facts
              </label>
              <textarea
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                rows={2}
                placeholder="Key dates, impugned notices, arbitration clauses, or court orders to be challenged..."
                className="w-full px-3 py-2 bg-slate-850 border border-slate-750 rounded-xl text-xs text-white placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-amber-500/50 resize-none"
              />
            </div>

            {/* Simulated Encrypted Document Vault Dropzone */}
            <div className="p-3 rounded-xl bg-slate-850/60 border border-dashed border-slate-700/80 text-center space-y-1">
              {hasUploadedFile ? (
                <div className="flex items-center justify-between px-3 py-1.5 rounded-lg bg-emerald-500/10 border border-emerald-500/20 text-xs text-emerald-400">
                  <div className="flex items-center gap-2">
                    <FileText className="w-4 h-4" />
                    <span className="font-semibold">{fileName}</span>
                  </div>
                  <button
                    type="button"
                    onClick={() => setHasUploadedFile(false)}
                    className="text-slate-400 hover:text-white text-[10px]"
                  >
                    Remove
                  </button>
                </div>
              ) : (
                <div 
                  onClick={handleSimulateFileUpload}
                  className="cursor-pointer py-2 hover:text-amber-300 transition-colors"
                >
                  <UploadCloud className="w-6 h-6 mx-auto text-slate-400 mb-1" />
                  <p className="text-xs font-semibold text-slate-300">
                    Click to attach preliminary brief, FIR, or impugned notice
                  </p>
                  <p className="text-[10px] text-slate-500">
                    Simulated 256-bit client vault upload • PDF, DOCX up to 25MB
                  </p>
                </div>
              )}
            </div>

            {/* Dynamic Retainer Estimate Summary */}
            <div className="p-3.5 rounded-xl bg-slate-950/80 border border-amber-500/30 flex items-center justify-between">
              <div>
                <div className="text-[10px] uppercase font-bold tracking-wider text-slate-400">
                  Indicative Counsel Retainer
                </div>
                <div className="text-base font-serif font-bold text-amber-400 font-mono">
                  {formattedRetainer}
                </div>
                <div className="text-[10px] text-slate-500">
                  Includes initial study, draft petition & 1st court appearance
                </div>
              </div>
              <div className="text-right text-[10px] text-slate-400">
                <span className="flex items-center gap-1 justify-end font-semibold text-slate-300">
                  <Lock className="w-3 h-3 text-amber-400" />
                  Privileged Matter
                </span>
                <span>No initial charge for intake screening</span>
              </div>
            </div>

            {/* Submit Button */}
            <button
              type="submit"
              disabled={loading}
              className="w-full py-3 px-4 rounded-xl bg-gradient-to-r from-amber-500 to-amber-600 hover:from-amber-400 hover:to-amber-500 text-slate-950 font-bold text-xs shadow-xl shadow-amber-500/10 transition-all flex items-center justify-center gap-2 disabled:opacity-50"
            >
              {loading ? (
                <span>Ingesting Matter & Running Conflict Check...</span>
              ) : (
                <>
                  <ShieldCheck className="w-4 h-4" />
                  <span>Submit Confidential Matter Evaluation</span>
                </>
              )}
            </button>
          </form>
        )}

      </div>
    </div>
  );
}
